/**
 * OrdoSidebar — renders the 10-item canonical sidebar driven by
 * `useProfile().profile.nav`.
 *
 * Source of truth for the item list + ordering is the server's
 * `/api/me/profile` response (which itself calls @ordo/access's
 * `resolveNavItems()`). This component is purely presentational — no
 * permission logic, no profile-code checks. Just rendering.
 *
 * Items are grouped into three zones (priority / work / admin), each
 * rendered as its own `AppSidebarSection` — Tasks gets its own top
 * group with a destructive-variant badge showing open task count, the
 * work zone (Orders, My Family, Recipes, ...) sits in the middle, and
 * Billing + Settings group at the bottom. Empty zones are skipped.
 * Production Records wears the same badge with the number of campus-days
 * whose record is still incomplete (the campus tabs' counts, summed), so
 * a school admin sees the backlog from anywhere in the app (Abner, Sep 15).
 *
 * The footer slot mounts `AppSidebarUserMenu` from `@ordo/ui/app-sidebar`,
 * which carries the avatar menu's full chrome (My Profile / theme
 * toggle / sign out). Per Plan 03 mockup feedback #5 ("Account belongs
 * in the sidebar, fixed to bottom-left, per `@ordo/ui` conventions"),
 * we do NOT render a duplicate avatar in the topbar.
 *
 * The pinned header slot mounts `AppSidebarBrand` with the `OrdoLogo`
 * wordmark — clicking navigates home via react-router (no full reload).
 */

import { getColorMode } from '@ordo/auth'
import {
  AppSidebar as AppSidebarRoot,
  AppSidebarBrand,
  AppSidebarFooter,
  AppSidebarItem,
  AppSidebarNav,
  AppSidebarSection,
  AppSidebarUserMenu,
} from '@ordo/ui/app-sidebar'
import { Badge } from '@ordo/ui/badge'
import { OrdoLogo } from '@ordo/ui/logos'
import { Separator } from '@ordo/ui/separator'
import { useColorMode } from '@ordo/ui/use-dark-mode'
import { type LucideIcon, UtensilsCrossed } from 'lucide-react'
import { useIntl } from 'react-intl'
import { Link, useLocation } from 'react-router'

import { sumPendingDays, useCampusSummaryQuery } from '@/api/production-records'
import { useSchoolOnboardingGate } from '@/api/school-onboarding'
import { useTaskCountsQuery } from '@/api/tasks'
import { CafeteriaLauncher, shouldShowCafeteriaLauncher } from '@/components/cafeteria/launcher'
import { useChefTempLogTasks } from '@/lib/chefs/temp-log'
import { pickHomeNavItem } from '@/lib/nav-home'
import { NAV_ICONS } from '@/lib/nav-icons'
import { sidebarRowBadgeCount } from '@/lib/sidebar-badges'
import { useAuth } from '@/stores/auth'
import type { ProfileNavItem, ProfileNavZone } from '@/stores/profile'
import { useProfile } from '@/stores/profile'

// Slug → icon mapping lives in `lib/nav-icons.ts`, shared with the pages
// whose headers mirror their nav row (the chef kitchen-list pages).
const ICONS: Record<string, LucideIcon> = NAV_ICONS

function FallbackIcon() {
  return <UtensilsCrossed className="h-5 w-5 md:h-4 md:w-4" />
}

function isActive(pathname: string, href: string, homeTargetHref: string | null): boolean {
  if (href === '/') {
    return pathname === '/'
  }
  // At `/` we render the persona's default page inline (see routes/
  // `HomePage`). Highlight the nav item that matches that target so the
  // active row tracks the visible content even though the URL is `/`.
  if (pathname === '/' && homeTargetHref === href) {
    return true
  }
  return pathname === href || pathname.startsWith(`${href}/`)
}

interface OrdoSidebarProps {
  onClose?: () => void
}

export function OrdoSidebar({ onClose }: OrdoSidebarProps) {
  const { pathname } = useLocation()
  const { user, signOut } = useAuth()
  const { profile } = useProfile()
  // `fallback` keeps the segmented control in step with what
  // `applyDefaultThemePreference` already painted: on a device that has
  // never toggled, localStorage is empty on purpose (that is what keeps
  // the app tracking the account preference), so without this the control
  // would highlight System while the page renders the account's Dark.
  const { preference: colorMode, setPreference: setColorMode } = useColorMode({
    fallback: getColorMode(user) ?? null,
  })
  const intl = useIntl()
  const variant = onClose ? 'drawer' : 'desktop'

  // Cafeteria Mode is launcher-only (no numbered nav item), so there's
  // nothing to filter out of `items`. The launcher surfaces for
  // kiosk-paired tablets, CAFETERIA_WORKER, SCHOOL_ADMIN, and ORDO_ADMIN
  // — all read off `profile.profileCodes`, which an Ordo-takeover session
  // already reports as the impersonated identity.
  const showCafeteriaLauncher = shouldShowCafeteriaLauncher(profile)
  const items = profile?.nav.items ?? []

  // While a school admin is gated (onboarding not yet provisioned), the
  // Orders / Production Records / Settings rows stay CLICKABLE but render the
  // gate/waiting screen via `OnboardingGateGuard` — so the school can always
  // get back to the "we're setting you up" status from any nav item, rather
  // than facing dead, greyed-out rows with no way to the gate home.
  const { gated: onboardingGated } = useSchoolOnboardingGate()

  // Mirror routes/`HomePage` — at `/` we render the first non-priority
  // nav item's page inline. The sidebar's active highlight follows the
  // same target so the user sees Orders highlighted at `/` when Orders
  // is the persona default. When gated, `/` is the onboarding gate (not a
  // nav item), so nothing is highlighted.
  const homeTarget = pickHomeNavItem(items)
  const homeTargetHref = onboardingGated ? null : (homeTarget?.href ?? null)

  // Each zone renders as its own `AppSidebarSection`. Sections replace
  // the previous divider-between-zones approach: a Tasks-only top
  // section visually separates "things blocking you" from the rest of
  // the app, and admin (Billing / Settings) sits in its own bottom
  // group. The server already tags every nav item with `zone`; we
  // just project the canonical order onto sections here.
  const zones: ProfileNavZone[] = ['priority', 'work', 'admin']
  const itemsByZone = new Map<ProfileNavZone, ProfileNavItem[]>()
  for (const zone of zones) {
    itemsByZone.set(zone, [])
  }
  for (const item of items) {
    itemsByZone.get(item.zone)?.push(item)
  }

  // Task count drives the destructive-variant badge on the Tasks row.
  // The hook polls every 30s (same cadence as the Tasks page chip strip)
  // so the badge stays fresh without a websocket. Always called regardless
  // of whether Tasks is in the user's nav — rules-of-hooks compliance.
  //
  // The `__all` sentinel IS the open-task total (summing every row would
  // double-count via the `overdue`/`__all` sentinels). Chefs' per-shipment
  // CHEF_SUBMIT_TEMP_LOG tasks collapse into one inbox row per DAY
  // (see `mergeChefTempLogDayRows`), so the badge swaps that slice for
  // the day count — matching what the Tasks page actually lists.
  const { data: taskCountsData } = useTaskCountsQuery()
  const counts = taskCountsData?.counts ?? []
  const allCount = counts.find((c) => c.subKind === '__all')?.count ?? 0
  const tempLogTaskCount = counts.find((c) => c.subKind === 'CHEF_SUBMIT_TEMP_LOG')?.count ?? 0
  const tempLogDays = useChefTempLogTasks(tempLogTaskCount > 0)
  const openTaskCount = allCount - tempLogTaskCount + tempLogDays.tasks.length

  // Production Records badge: campus-days still carrying an open Transport
  // logs or Service records task (`/campus-summary`, the same numbers the
  // page's campus tabs show), summed and polled on the Tasks cadence. Only
  // fetched when the row is in this user's nav, so parents, chefs and
  // cafeteria workers never call the production-records route.
  const hasProductionRecordsNav = items.some((item) => item.slug === 'production-records')
  const { data: campusSummary } = useCampusSummaryQuery(hasProductionRecordsNav, { refetchInterval: 30_000 })
  const pendingProductionDays = sumPendingDays(campusSummary?.campuses)

  return (
    <AppSidebarRoot variant={variant} onNavigate={onClose} style={{ fontFamily: 'var(--font-sans)' }}>
      <AppSidebarBrand>
        <Link to="/" onClick={onClose} aria-label="Ordo home" className="flex min-w-0 items-center">
          {/* -ml-0.5 counters the wordmark SVG's intrinsic left viewBox
              whitespace (the glyph starts at x=64.2 of an 898-wide viewBox),
              so the visible "ordo" left edge lands on the nav icon column. */}
          <OrdoLogo className="-ml-0.5 h-8 w-auto" />
          {profile?.portalPersona === 'supplier' ? (
            <span className="ml-3 text-sm font-medium text-muted-foreground">Supplier</span>
          ) : null}
        </Link>
      </AppSidebarBrand>

      <AppSidebarNav>
        {(() => {
          const visibleZones = zones.filter((zone) => (itemsByZone.get(zone) ?? []).length > 0)
          return visibleZones.map((zone, idx) => {
            const zoneItems = itemsByZone.get(zone) ?? []
            // Thin horizontal rule between consecutive sections — Tasks ↔ Orders
            // group ↔ Settings group. Matches the visual rhythm of pre-section
            // dividers without coupling per-app sidebar render code to the
            // server-computed `dividers` array.
            return (
              <div key={zone}>
                {idx > 0 ? <Separator className="my-2" /> : null}
                <AppSidebarSection>
                  {zoneItems.map((item) => (
                    <SidebarRow
                      key={item.slug}
                      item={item}
                      pathname={pathname}
                      homeTargetHref={homeTargetHref}
                      taskCount={openTaskCount}
                      pendingProductionDays={pendingProductionDays}
                    />
                  ))}
                </AppSidebarSection>
              </div>
            )
          })
        })()}
        {showCafeteriaLauncher ? (
          // Cafeteria Mode entry point sits directly below the last nav item
          // (not pinned to the footer) so it reads as part of the main nav.
          <div className="px-2 pt-1">
            <CafeteriaLauncher />
          </div>
        ) : null}
      </AppSidebarNav>

      <AppSidebarFooter className="border-t-0 px-0 py-0">
        <div className="border-t border-sidebar-border px-3 py-2">
          {/* No profile entry — My profile lives on /settings/account
              (sidebar Settings) per PROD-27; omitting onProfile hides
              the row in AppSidebarUserMenu. */}
          <AppSidebarUserMenu
            user={user}
            colorMode={colorMode}
            onColorModeChange={setColorMode}
            signOutLabel={intl.formatMessage({ id: 'avatar.sign-out' })}
            onSignOut={() => {
              void signOut()
            }}
          />
        </div>
      </AppSidebarFooter>
    </AppSidebarRoot>
  )
}

function SidebarRow({
  item,
  pathname,
  homeTargetHref,
  taskCount,
  pendingProductionDays,
  disabled,
}: {
  item: ProfileNavItem
  pathname: string
  homeTargetHref: string | null
  taskCount: number
  pendingProductionDays: number
  disabled?: boolean
}) {
  const Icon = ICONS[item.slug] ?? null
  const intl = useIntl()
  // Server-provided label is the canonical English fallback; the i18n
  // key reuses the canonical slug so translations slot in without a
  // server change. `item.slug` is server-provided and unbounded, so it
  // cannot be typed against `MessageId` — membership is checked against the
  // loaded catalog instead, and a slug with no key falls through to
  // item.label unchanged. Never cast here: a cast is what lets a missing key
  // print the dotted id as visible text.
  const labelKey = `nav.${item.slug}`
  const label = labelKey in intl.messages ? intl.formatMessage({ id: labelKey }) : item.label
  // Tasks: open tasks; Production Records: incomplete campus-days. Same badge.
  const badgeCount = sidebarRowBadgeCount(item.slug, { taskCount, pendingProductionDays })
  const trailing =
    badgeCount !== null ? (
      <Badge variant="destructive" className="px-1.5 py-0 text-[10px] leading-4">
        {badgeCount}
      </Badge>
    ) : undefined
  const icon = Icon ? <Icon className="h-5 w-5 md:h-4 md:w-4" /> : <FallbackIcon />
  // Gated rows render greyed-out and non-navigable (no wrapped <Link>).
  if (disabled) {
    return (
      <AppSidebarItem disabled icon={icon} trailing={trailing}>
        {label}
      </AppSidebarItem>
    )
  }
  return (
    <AppSidebarItem asChild active={isActive(pathname, item.href, homeTargetHref)} icon={icon} trailing={trailing}>
      <Link to={item.href}>{label}</Link>
    </AppSidebarItem>
  )
}
